import { StrictMode, useEffect } from "react";
import type { ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "./lib/theme";
import { AuthProvider } from "./lib/auth";
import { ToastProvider, Toaster } from "./components/Toast";
import { Analytics } from "./components/Analytics";
import { App } from "./App";
import './styles/theme.css';
import "./index.css";

// The homepage stays auth-enabled so signed-in visitors get recognized and
// redirected to /dashboard (see Landing.tsx).
const PUBLIC_PATH_PREFIXES = ["/variants", "/docs", "/blog", "/privacy", "/terms", "/login", "/signed-out", "/pricing"];

function RouteAwareAuthProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const isPublic = PUBLIC_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );

  return <AuthProvider enabled={!isPublic}>{children}</AuthProvider>;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <HelmetProvider>
      <ThemeProvider>
        <BrowserRouter>
          <ScrollToTop />
          <RouteAwareAuthProvider>
            <ToastProvider>
              <Analytics />
              <App />
              <Toaster />
            </ToastProvider>
          </RouteAwareAuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </HelmetProvider>
  </StrictMode>,
);
